import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  Injector,
  SimpleChanges,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { BaseComponent, ConfirmRedeemModalComponent, SnackbarComponent } from '../../../../components';
import { MainTemplate } from '../../../../models/template';
import { ProfileService, StyleService } from '../../../../services';
import {
  ConversionType,
  RedemptionsConfigModel,
  RedemptionTemplateConfigModel,
  TierLevels,
} from '../../../../models/redemption-template.model';
import { RedemptionTemplateComponent } from '../redemption-template/redemption-template.component';
import { OverlayContainer } from '@angular/cdk/overlay';
import { RedemptionV2ModalComponent } from '../redemption-v2-modal/redemption-v2-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs';

import { TransactionLogService } from '../../../../services/transaction-log.service';
import * as uuid from 'uuid';

type RedeemTypeV2 = 'freePlay' | 'matchPlay' | 'directBet' | 'cashValue' | 'dining';

type RedeemOptionUI = {
  id: string;
  type: RedeemTypeV2;
  name: string;       
  imageLink?: string | null;
  voucherHtml?: string | null;
};

@Component({
  selector: 'app-redemption-v2-template',
  templateUrl: './redemption-v2-template.component.html',
  styleUrls: ['./redemption-v2-template.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RedemptionV2TemplateComponent
  extends RedemptionTemplateComponent
  implements OnInit, OnDestroy
{
  public override sourceBucket = '';
  public override selectedRedeem: string = '';

  getRedeemName(id: string): string {
    return this.redeemOption.find(o => o.id === id)?.name ?? '';
  }
  @ViewChild('redeemArea', { read: ElementRef }) redeemArea!: ElementRef<HTMLElement>;

  private _lastInitKey = '';

  //replaced with dynamic array
  public redeemOption1 = [
    {
      id: 'freePlay',
      name: 'Free Slot Play',
    },
    {
      id: 'matchPlay',
      name: 'Match Play',
    },
    {
      id: 'directBet',
      name: 'Direct Bet',
    },
  ];

  public redeemOption: RedeemOptionUI[] = [];

  public isRedeemPickerOpen = false;

public selectedRedeemOption: RedeemOptionUI | null = null;

  constructor(protected override readonly injector: Injector,
    private overlay: OverlayContainer, 
    protected override readonly dialog: MatDialog
  ) {
    super(injector);
  }
// OC-590
  protected override getConversioRate() {
  const page =
    this.mainTemplate.template.pageLinks[this.pageLinkIndex].templateSettings[
      this.selectTabIndex
    ];

  if (page.templateName !== this.tabType.RedemptionsV2) {
    return { rateFrom: 0, rateTo: 0 };
  }

  const templateConfig: RedemptionTemplateConfigModel =
    page.templateConfig as any;

  if (templateConfig?.conversionRate === 'tiered') {
    const tierConfig = this.getCurrentTieredRateConfig();

    if (tierConfig) {
      return {
        rateFrom: Number(tierConfig.rateFrom || 0),
        rateTo: Number(tierConfig.rateTo || 0),
      };
    }

    return { rateFrom: 0, rateTo: 0 };
  }

  return {
    rateFrom: Number(templateConfig.rateFrom || 0),
    rateTo: Number(templateConfig.rateTo || 0),
  };
}

protected override async adjustingRedemptionValues() {

  let balanceWithDraw: any = null;
  let balanceToUpdate: any = null;

  // to withdraw from (points source)
  switch (this.sourceBucket) {
    case 'tierPointBalance':
      balanceWithDraw = this.profileService.playerBalances.a_Tier;
      break;
    case 'compsBalance':
      balanceWithDraw = this.profileService.playerBalances.a_Comps;
      break;
  }

  const redeemType = this.getSelectedRedeemType();
  // to deposit to (ONLY relevant for freePlay special case)
  switch (redeemType) {
    case 'freePlay':
      balanceToUpdate = this.profileService.playerBalances.a_FreePlay;
      break;
    case 'matchPlay':
      balanceToUpdate = this.profileService.playerBalances.a_Comps;
      break;
    case 'directBet':
      balanceToUpdate = this.profileService.playerBalances.a_Tier;
      break;
    case 'cashValue':
    case 'dining':
      balanceToUpdate = this.profileService.playerBalances.a_Comps; // assuming cash acts like comps (confirm later)
      break;
  }

  return new Promise(async (resolve, reject) => {
    this.transactionVoucherId = uuid.v4();
    const transactionDateTime = new Date();
    const config = this.mainTemplate.template.pageLinks[this.pageLinkIndex]
      .templateSettings[this.selectTabIndex].templateConfig as any;

      //capture requested values before withdraw/deposit changes balance/source bucket
    const requestedConversionAmount = this.redemptionAmountValue;
    const requestedBalanceDeduction = this.calculatePointsToRedeem();

    const baseLogModel = {
      externalId: this.profileService.playerProfile.playerId,
      transactionType: 3,
      sourceBucket: this.sourceBucket,
      
      //use captured requested values
      balanceDeduction: requestedBalanceDeduction,
      conversionAmount: requestedConversionAmount,

      conversionType: this.selectedRedeemOption?.type ?? this.selectedRedeem,
      conversionLabel: this.getSelectedRedeemTitle(),
      voucherID: this.transactionVoucherId,
      firstName: this.profileService.playerProfile.playerProfile?.firstName,
      lastName: this.profileService.playerProfile.playerProfile?.lastName,
      kioskId: this.authService.kioskId ?? '',
      kioskName: this.authService.preparedKioskDeviceId,
      transactionDateTime,

      rateFrom: this.getConversioRate().rateFrom,
      rateTo: this.getConversioRate().rateTo,
    };

    let statusLog = '';

    const onBadRequest = async (msg: string) => {
      this.redemptionAmountValue = 0;
      this.isLoading = false;

      this.snackbar.openFromComponent(SnackbarComponent, {
        panelClass: 'error',
        duration: 3000,
        horizontalPosition: 'end',
        data: { type: 'error', message: msg },
      });

      try {
        await this.transactionLogService.postTransactionLog({
          ...baseLogModel,
          redemptionStatus: 9,
          statusMessage: statusLog + `Failed: ${msg}`,
          webhookStatus: 4,
        });
      } catch (logErr) {
        console.error('Transaction log failed (non-blocking):', logErr);

        //save failed transaction log locally so it can be retried when browser is online again
        let pendingLogs: any[] = [];
        try {
          pendingLogs = JSON.parse(localStorage.getItem('pendingTransactionLogs') ?? '[]');
        } catch {
          pendingLogs = [];
        }
        pendingLogs.push({
          ...baseLogModel,
          redemptionStatus: 9,
          statusMessage: statusLog + `Failed: ${msg}`,
          webhookStatus: 4,
          createdAt: new Date().toISOString()
        });
        localStorage.setItem('pendingTransactionLogs', JSON.stringify(pendingLogs));
      }

      this.printService.setIsPrinting(false, 'Redemption v2 adjustingRedemptionValues onBadRequest');
      reject(msg);
    };

    this.isLoading = true;
     //originally owned here since adjustingRedemptionValues drove the flow, 
  // but moved to confirm-redeem-modal which has full context on whether printing actually happens
  // this.printService.setIsPrinting(true, 'Redemption v2 adjustingRedemptionValues');

    // WITHDRAW from source bucket
    let adjRes: any;
    try {
      adjRes = await this.profileService.doBalanceAdjustment(
        this.playerProfile?.playerId,
        //use captured requested deduction
        requestedBalanceDeduction,
        this.sourceBucket,
        'D',
        balanceWithDraw,
        '',
        this.dailyLimit,
        this.redemptionAmountValue.toString(),
        this.accountId
      );
    } catch (networkErr: any) {

      //normalize ugly profile.service crash message into a clean timeout message
      const isTimeoutOrCrash = !networkErr?.message || networkErr?.message?.includes('Cannot read properties');
      await onBadRequest(isTimeoutOrCrash ? 'Network error: no response from server' : (networkErr?.message ?? 'Network error: no response from server'));

      return;
    }

    // if withdraw failed
    //extract UC v2 error message which comes in message/errors[]/result fields
    // instead of status/errorDetails, so callers see it correctly without changing profile.service.ts
    // exclude 'ok' success marker from being treated as UC v2 error message
      const ucV2ErrorMsg = !adjRes?.status && !adjRes?.error
        ? (adjRes?.message || adjRes?.errors?.[0]?.message || (typeof adjRes?.result === 'string' && adjRes.result.length > 0 && adjRes.result !== 'ok' ? adjRes.result : null) || null)
        : null;

    const effectiveError = adjRes?.error || ucV2ErrorMsg || null;
    const effectiveStatus = adjRes?.status || null;

    //for UC v2, a response with no status/error/result is a silent success (transactionId only, message stripped by UC directive)
      const isUcV2SilentSuccess = this.profileService.isUcSecondVersion && !effectiveError && !effectiveStatus && adjRes?.result === undefined;
      const withdrawFailed = !isUcV2SilentSuccess && (effectiveError || effectiveStatus ||
        (!effectiveError && !effectiveStatus && !adjRes?.result));

    if (withdrawFailed) {
      await onBadRequest(effectiveStatus || effectiveError || 'No response from server');
      return;
    }

    statusLog += 'Withdraw completed. ';

    // DEPOSIT only for Free Slot Play only
    if (redeemType === 'freePlay') {
      //catch network-level failures on freePlay deposit so transaction log can still be attempted/saved
      let res: any;
      try {
        res = await this.profileService.doBalanceAdjustment(
          this.playerProfile?.playerId,
          requestedConversionAmount,
          'freePlay',
          'C',
          balanceToUpdate
        );
      } catch (networkErr: any) {
        statusLog += 'Deposit failed/no response. MANUAL INTERVENTION REQUIRED. ';
        await onBadRequest(networkErr?.message ?? 'Network error: no response from server');
        return;
      }

      // if deposit failed => rollback withdraw
      if (res?.status || res?.error) {
        const rollbackRes =  await this.profileService.doBalanceAdjustment(
          this.playerProfile?.playerId,
          requestedBalanceDeduction,
          this.sourceBucket,
          'C',
          balanceWithDraw,
          '',
          this.dailyLimit,
          this.redemptionAmountValue.toString(),
          this.accountId
        );

        if (rollbackRes?.error || rollbackRes?.status) {
            statusLog += "Rollback failed. MANUAL INTERVENTION REQUIRED. Failed: deposit for a free play ";
        }
        else {
              statusLog += "Rollback completed. ";
        }

        await onBadRequest(res.status ? res.status : res.error);
        return;
      }
      statusLog += 'Deposit completed. ';
    }

  //use captured requested values for completed success log
  const completedConversionAmount = requestedConversionAmount;
  const completedBalanceDeduction = requestedBalanceDeduction;
  
    // success
    this.redemptionAmountValue = 0;
    await this.profileService.getPlayerBalance(this.playerProfile.playerId);
    this.isLoading = false;

  //originally owned here since adjustingRedemptionValues drove the flow, 
  // but moved to confirm-redeem-modal which has full context on whether printing actually happens
 //this.printService.setIsPrinting(false, 'Redemption v2 adjustingRedemptionValues success');
    
    try {
      await this.transactionLogService.postTransactionLog({
        ...baseLogModel,
        redemptionStatus: 8,
        statusMessage: 'Success: ' + statusLog.trim(),
        conversionAmountCompleted: completedConversionAmount,
        balanceDeductionCompleted: completedBalanceDeduction,
        webhookStatus: 3,
      });
    } catch (logErr) {
      console.error('Transaction log failed (non-blocking):', logErr);

      //save successful transaction log locally so it can be retried when browser is online again
      let pendingLogs: any[] = [];
      try {
        pendingLogs = JSON.parse(localStorage.getItem('pendingTransactionLogs') ?? '[]');
      } catch {
        pendingLogs = [];
      }
      pendingLogs.push({
        ...baseLogModel,
        redemptionStatus: 8,
        statusMessage: 'Success: ' + statusLog.trim(),
        conversionAmountCompleted: completedConversionAmount,
        balanceDeductionCompleted: completedBalanceDeduction,
        webhookStatus: 3,
        createdAt: new Date().toISOString()
      });
      localStorage.setItem('pendingTransactionLogs', JSON.stringify(pendingLogs));
    }
  
    resolve(true);
  });
}

  private getSelectedRedeemType(): RedeemTypeV2 | '' {
  // V2 (dynamic array)
  const t = this.selectedRedeemOption?.type;
  if (t) return t;

  // V1 / legacy
  const id = this.selectedRedeem;
  if (id === 'freeSlotPlay') return 'freePlay'; // legacy alias
  if (id === 'freePlay') return 'freePlay';
  if (id === 'matchPlay') return 'matchPlay';
  if (id === 'directBet') return 'directBet';
  if (id === 'cashValue') return 'cashValue';
  if (id === 'dining') return 'dining';

  return '';
}

  override ngOnInit(): void {
    this.sourceBucket = this.tab.templateConfig.sourceBucket;
    this.tryInitRedeemOptions();
    this.applyDropdownColor();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.tryInitRedeemOptions(); // will run when parent updates inputs
  }

  private isReadyForInit(): boolean {
    const page = this.getCurrentPage();
    return !!page && page.templateName === this.tabType.RedemptionsV2;
  }

  private tryInitRedeemOptions(): void {
    if (!this.isReadyForInit()) return;

    // avoid redoing work on the same page repeatedly
    const key = `${this.pageLinkIndex}:${this.selectTabIndex}`;
    if (this._lastInitKey === key) return;
    this._lastInitKey = key;

    this.initRedeemOptionsFromBuilder();
  }

  //replaced with dynamic array
  private initRedeemOptionsFromBuilder1(): void {
  const cfg = this.getCurrentConfig();

  const defaults: Record<string, string> = {
    freePlay: 'Free Slot Play',
    matchPlay: 'Match Play',
    directBet: 'Direct Bet',
  };

  const options: { id: string; name: string }[] = [];
  if (cfg?.showFreeSlot)  options.push({ id: 'freePlay', name: (cfg?.inputFreeSlot  ?? '').toString().trim() || defaults.freePlay });
  if (cfg?.showMatchPlay) options.push({ id: 'matchPlay',    name: (cfg?.inputMatchPlay ?? '').toString().trim() || defaults.matchPlay });
  if (cfg?.showDirectBet) options.push({ id: 'directBet',    name: (cfg?.inputDirectBet ?? '').toString().trim() || defaults.directBet });

  this.redeemOption1 = options.length ? options : [
    { id: 'freePlay', name: defaults.freePlay },
    { id: 'matchPlay',    name: defaults.matchPlay },
    { id: 'directBet',    name: defaults.directBet },
  ];
}

private initRedeemOptionsFromBuilder(): void {
  const cfg = this.getCurrentConfig() as any;

  const v2 = (cfg?.redeemOptions ?? []) as any[];

  const fromV2: RedeemOptionUI[] = v2
    .filter(o => !!o && o.enabled !== false)
    .map(o => ({
      id: (o.id ?? o.type ?? '').toString(),
      type: (o.type ?? 'cashValue') as RedeemTypeV2,
      name: (o.label ?? '').toString().trim(),
      imageLink: o?.image?.imageLink ?? null,

      voucherHtml: o?.voucherHtml ?? null,

    }))
    // must have an id and label
    .filter(o => o.id.length > 0 && o.name.length > 0);

  if (fromV2.length) {
    this.redeemOption = fromV2;

    // if current selection is not valid anymore, reset
    if (this.selectedRedeem && !this.redeemOption.some(o => o.id === this.selectedRedeem)) {
      this.selectedRedeem = '';
    }

    this.selectedRedeemOption = this.redeemOption.find(o => o.id === this.selectedRedeem) ?? null;


    return; //  done, no legacy needed
  }

  // Legacy fallback (old v2 fields) ---
  const defaults: Record<string, string> = {
    freePlay: 'Free Slot Play',
    matchPlay: 'Match Play',
    directBet: 'Direct Bet',
    cashValue: 'Cash Voucher',
    dining: 'Dining',
  };

  const legacy: RedeemOptionUI[] = [];

  if (cfg?.showFreeSlot) {
  legacy.push({
    id: 'freePlay',
    type: 'freePlay',
    name: (cfg?.inputFreeSlot ?? '').toString().trim() || defaults.freePlay,
    });
  }
  if (cfg?.showMatchPlay) {
    legacy.push({
      id: 'matchPlay',
      type: 'matchPlay',
      name: (cfg?.inputMatchPlay ?? '').toString().trim() || defaults.matchPlay,
    });
  }
  if (cfg?.showDirectBet) {
    legacy.push({
      id: 'directBet',
      type: 'directBet',
      name: (cfg?.inputDirectBet ?? '').toString().trim() || defaults.directBet,
    });
  }

  // If legacy ever had cash/dining flags, we will support them here later.

  this.redeemOption = legacy.length
    ? legacy
    : [
        { id: 'freePlay', type: 'freePlay', name: defaults.freePlay },
        { id: 'matchPlay', type: 'matchPlay', name: defaults.matchPlay },
        { id: 'directBet', type: 'directBet', name: defaults.directBet },
      ];

    this.selectedRedeemOption =
    this.redeemOption.find(o => o.id === this.selectedRedeem) ?? null;


}


private getCurrentPage() {
  return this.mainTemplate?.template?.pageLinks?.[this.pageLinkIndex]
    ?.templateSettings?.[this.selectTabIndex];
}

private getCurrentConfig(): any {
  return this.getCurrentPage()?.templateConfig as any;
}


  private applyDropdownColor(): void {
    const color =
      this.tab?.configurations?.main?.dropdown?.backgroundPane?.color
      // this.styleService.getColor(this.tab?.configurations?.main?.dropdown?.background)
      || '#E6F0CF';

    this.overlay.getContainerElement()
        .style.setProperty('--dropdown-bg', color);
  }

  onRedeemOpened(open: boolean) {
  if (!open) return;
  this.applyDropdownColor();

  const panel = document.querySelector(
    '.cdk-overlay-pane .mat-winloss-select.mdc-menu-surface.mat-mdc-select-panel'
  ) as HTMLElement | null;

  if (panel) {
    const color =
      this.tab?.configurations?.main?.dropdown?.backgroundPane?.color || '#E6F0CF';
    panel.style.backgroundColor = color; // direct override as a fallback
  }
}

openRedeemOptionPicker(): void {

  const el = this.redeemArea?.nativeElement;
  const rect = el ? el.getBoundingClientRect() : null;

  const allowTabs =
    !!this.tab?.configurations?.modal?.isTabButtonsAllowed;

  const top = rect ? Math.max(0, Math.floor(rect.top)) : 0;
  const height = Math.max(0, window.innerHeight - top);

  const dialogRef = this.dialog.open(RedemptionV2ModalComponent, {
    panelClass: 'redeem-v2-picker-dialog',
    backdropClass: 'redeem-v2-picker-backdrop',
    hasBackdrop: true,
    autoFocus: false,
    restoreFocus: false,

    width: '100vw',
    maxWidth: '100vw',
    height: `${height}px`,

  
    position: { top: `${top}px`, left: '0px' },

    data: {
      options: this.redeemOption,
      selectedId: this.selectedRedeem,
      tab: this.tab,
      mainTemplate: this.mainTemplate,
      groupByType: allowTabs,   // tabs if tabs needed
      noOptionsMessage: 'No redemption option currently available.',
    },
  });

  dialogRef.afterClosed().pipe(take(1)).subscribe((picked: RedeemOptionUI | null) => {
    if (!picked) return;

    const changed = this.selectedRedeem !== picked.id;

    this.selectedRedeem = picked.id;
    this.selectedRedeemOption = picked;

    if (changed) {
    this.redemptionAmountValue = 0; 
  }
  });
}

protected getSelectedRedeemTitle(): string {

  // If using new V2 array, useit first 
  const fromList = this.redeemOption.find(o => o.id === this.selectedRedeem);
  if (fromList?.name) return fromList.name;

  const cfg: any = this.tab?.templateConfig ?? {};
  if (this.selectedRedeem === 'freePlay')  return cfg?.inputFreeSlot  ?? 'Free Slot Play';
  if (this.selectedRedeem === 'matchPlay') return cfg?.inputMatchPlay ?? 'Match Play';
  if (this.selectedRedeem === 'directBet') return cfg?.inputDirectBet ?? 'Direct Bet';

   // new types (when selectedRedeem equals type in old setup)
  if (this.selectedRedeem === 'cashValue') return 'Cash Voucher';
  if (this.selectedRedeem === 'dining')   return 'Dining';

  const matchedOption = this.redeemOption.find(o => o.id === this.selectedRedeem);
  return matchedOption?.name ?? '';
}

public override getConversionType() {
  //need to keep the original call too, form the Redemption Template
  const page = this.mainTemplate.template.pageLinks[this.pageLinkIndex]
    .templateSettings[this.selectTabIndex] as any;

  const type: string | undefined = page?.templateConfig?.conversionType;
  if (type) {
    return this.conversionType?.find((v: any) => v.value === type);
  }
//V2
if (this.selectedRedeem) {
  const fromList = this.conversionType?.find(v => v.value === this.selectedRedeem);
  if (fromList) return fromList;

  return { title: this.getSelectedRedeemTitle(), value: this.selectedRedeem };
}
  // no card for matchPlay/directBet (yet)
  return undefined; // NOT null
}


public override clearRedemptionAmount() {
    this.redemptionAmountValue = 0;

    this.selectedRedeem = '';
    this.selectedRedeemOption = null;
  }
// OC-590
private getCurrentTieredRateConfig(): any {
  const config = this.mainTemplate.template.pageLinks[this.pageLinkIndex]
    .templateSettings[this.selectTabIndex].templateConfig as any;

  if (config?.conversionRate !== 'tiered') {
    return null;
  }

  const tieredRates = config?.tiredConversionRate || [];

  const currentClub = this.playerProfile?.club?.currentClub;
  const gamingTierLevel = this.playerProfile?.gamingInfo?.tierLevel;

  const playerTierId =
    currentClub?.id?.toString()?.trim() ||
    gamingTierLevel?.id?.toString()?.trim() ||
    '';

  const playerTierName =
    currentClub?.name?.toString()?.trim() ||
    gamingTierLevel?.name?.toString()?.trim() ||
    gamingTierLevel?.status?.toString()?.trim() ||
    '';

  const matchedTier = tieredRates.find((tier: any) => {
    const tierGuid = tier?.guid?.toString()?.trim();
    const tierLevel = tier?.tierLevel?.toString()?.trim();

    return (
      (!!playerTierName && tierLevel === playerTierName) ||
      (!!playerTierId && tierGuid === playerTierId) ||
      (!!playerTierId && tierLevel === playerTierId)
    );
  });

  return matchedTier;
}
// OC-590
private getNumberOrFallback(value: any, fallbackValue: any): number {
  if (value === null || value === undefined || value === '') {
    return Number(fallbackValue || 0);
  }

  const numericValue = Number(value);

  return isNaN(numericValue) ? Number(fallbackValue || 0) : numericValue;
}
// OC-590
private getRedemptionLimitConfig(): any {
  const config = this.mainTemplate.template.pageLinks[this.pageLinkIndex]
    .templateSettings[this.selectTabIndex].templateConfig as any;

  if (config?.conversionRate !== 'tiered') {
    return {
      ...config,
      minValue: Number(config?.minValue || 0),
      maxValue: Number(config?.maxValue || 0),
      dailyLimit: Number(config?.dailyLimit || 0),
    };
  }

  const tierConfig = this.getCurrentTieredRateConfig();

  if (!tierConfig) {
    return {
      ...config,
      minValue: Number(config?.minValue || 0),
      maxValue: Number(config?.maxValue || 0),
      dailyLimit: Number(config?.dailyLimit || 0),
    };
  }

  return {
    ...config,
    minValue: this.getNumberOrFallback(tierConfig.minValue, config?.minValue),
    maxValue: this.getNumberOrFallback(tierConfig.maxValue, config?.maxValue),
    dailyLimit: this.getNumberOrFallback(tierConfig.dailyLimit, config?.dailyLimit),
  };
}

public override openRedeemConfirmation(): void {

  if (!this.selectedRedeem || !this.redemptionAmountValue) return;

  const voucherHtml = this.selectedRedeemOption?.voucherHtml ?? '';
  const selectedRedeemId = this.selectedRedeemOption?.id ?? this.selectedRedeem;
  const voucherType = this.selectedRedeemOption?.type ?? '';

  const config = this.mainTemplate.template.pageLinks[this.pageLinkIndex]
    .templateSettings[this.selectTabIndex].templateConfig as any;
// OC-590
const limitConfig = this.getRedemptionLimitConfig();

if (limitConfig?.minValue && this.redemptionAmountValue < limitConfig.minValue) {
  const substituted = this.fillLimitTokens(limitConfig.ivalidMinAmountText, limitConfig);

  return this.openInfoBlock(
    limitConfig.ivalidMinAmountTitle,
    substituted,
    '.content.info-modal-content .message-text {color: #F14823;}'
  );
}
// OC-590
if (limitConfig?.maxValue && this.redemptionAmountValue > limitConfig.maxValue) {
  const substituted = this.fillLimitTokens(limitConfig.ivalidMaxAmountText, limitConfig);

  return this.openInfoBlock(
    limitConfig.ivalidMaxAmountTitle,
    substituted,
    '.content.info-modal-content .message-text {color: #F14823;}'
  );
}

this.dailyLimit = limitConfig.dailyLimit?.toString();
this.accountId = config.accountId;

  // Only difference from v1 Redemption:
  // Prefer builder title if present (v1), otherwise use current v2 choice.
  const titleForModal =
    config.conversionTypeTitle ?? this.getSelectedRedeemTitle();
  const conversionTypeForModal =
    config.showConversionValue && titleForModal ? `${titleForModal}.` : '.';

  const selectedOpt1 =
  this.selectedRedeemOption
    ?? this.redeemOption?.find(o => o.id === this.selectedRedeem)
    ?? null;  

    const selectedOpt =
    this.selectedRedeemOption
    ?? this.redeemOption.find(o => String(o.id) === String(this.selectedRedeem) || o.type === this.selectedRedeem)
    ?? null;

  this.dialog.open(ConfirmRedeemModalComponent, {
    panelClass: 'profile-dialog',
    backdropClass: 'backDropClass',
    height: '18vh',
    width: '100vw',
    data: {
      callback: async () => {
        await this.adjustingRedemptionValues();
      },
      playerId: this.playerProfile.playerId,
      redeemPrefix: config.exchangeText,
      redeemValue: this.redemptionAmountValue,
      pointToReedem: this.calculatePointsToRedeem(),
      pointsRate: this.getConversioRate(),
      mainTemplate: this.mainTemplate,
      pageIndex: this.pageLinkIndex,
      templateIndex: this.selectTabIndex,

      conversionType: conversionTypeForModal,

      //period-free type for printing
      voucherType: titleForModal || '',

      selectedRedeemId: this.selectedRedeem,

      selectedRedeemOption: selectedOpt,
      redeemOptions: this.redeemOption,

      currentTemplate:
        this.mainTemplate.template.pageLinks[this.pageLinkIndex]
          .templateSettings[this.selectTabIndex],
      styles: (
        this.mainTemplate.template.pageLinks[this.pageLinkIndex]
          .templateSettings[this.selectTabIndex]
          .configurations as unknown as RedemptionsConfigModel
      ).popUp,
    },
  });
}

public override addMaxAmount() {

    if (this.isEmptyConversionRate) {
      return;
    }

    const { rateFrom, rateTo } = this.getConversioRate();
    const pointsRatio = rateFrom / rateTo;
    if (isNaN(pointsRatio) || pointsRatio <= 0) {
      return;
    }

    const sourceAmount = this.getSourceBucketAmount(this.sourceBucket); 
    if (sourceAmount == null) return;

    const maxAmount = Math.floor(sourceAmount / pointsRatio);
    this.redemptionAmountValue = Math.max(0, maxAmount);

  }

private fillLimitTokens(text: string | undefined, config: any): string {
  if (!text) return text as any;

  // numbers only (leave the $ hard-coded in the message text)
  const min = Number(config?.minValue ?? '').toLocaleString('en-US');
  const max = Number(config?.maxValue ?? '').toLocaleString('en-US');

  return text
    // both {{minAmount}}  and {{maxAmount}} 
    .replace(/\{\{\s*minAmount\s*\}\}/gi, min)
    .replace(/\{\{\s*maxAmount\s*\}\}/gi, max);
}

getSourceBucketAmount(sourceBucket: any): number | null {
    const raw = this.dataPointService.getValueByDataPointProperty(
      this.getSourceBucketUcValue(sourceBucket)
    );

    if (raw == null) return null;

    // to string + trim
    let value = raw.toString().trim();

    // keep only digits, separators and minus for negative amounts
    value = value.replace(/[^\d.,-]/g, '');

    // European-style decimal- e.g. "17.089,99" - need to take into consideration to avoid issues as in OC-100
    const hasCommaDecimal = /,\d{1,2}$/.test(value);

    if (hasCommaDecimal) {
      // remove thousand separators "." and use "," as decimal
      value = value.replace(/\./g, '');
      value = value.replace(/,/g, '.');
    } else {
      // US style: remove thousand separators ","
      value = value.replace(/,/g, '');
    }

    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  }

public override calculatePointsToRedeem(): number {
  const { pointsNeeded, accountBalance } = this.calculateNeededPointsToRedeem(
    this.redemptionAmountValue
  );
  // If user typed more than they have, we show 0 as per Alex
  //otherwise, return needed points - pointsNeeded
  return pointsNeeded > accountBalance ? 0 : pointsNeeded;
}

  public override addRedemptionValue(value: number) {
    var sourceBuckeTemp = 0;

    switch (this.sourceBucket) {
    case 'tierPointBalance':
      sourceBuckeTemp = this.profileService.playerAccountTierBalance || 0;
      break;
    case 'compsBalance':
      sourceBuckeTemp = this.profileService.playerAccountCompsBalance || 0;
      break;
    default:
      // If no specific bucket, we shoulduse points balance as fallback to keep old behaviour just in case
      sourceBuckeTemp = this.profileService.playerAccountPointBalance || 0;
      break;
  }

    if (this.isEmptyConversionRate || !this.selectedRedeem.length || sourceBuckeTemp  < 0) {
      return;
    }
    const pointsNeeded = this.calculateNeededPointsToRedeem(
      Number(this.redemptionAmountValue.toString() + value)
    ).pointsNeeded;

    if (isNaN(pointsNeeded)) {
      return;
    }

     // Use sourceBucket instead of playerAccountPointBalance
    const isValueMoreThanPoints = pointsNeeded > sourceBuckeTemp;

    if (isValueMoreThanPoints) {
      this.addMaxAmount();
      return;
    }
    this.redemptionAmountValue = Number(
      this.redemptionAmountValue.toString() + value
    );
  }
  

protected override calculateNeededPointsToRedeem(dollarsValue: number)  {
  const accountBalance =
    this.getSourceBucketAmount(this.sourceBucket) ?? 0;

  if (this.isEmptyConversionRate) {
    return {
      pointsNeeded: 0,
      accountBalance,
    };
  }

  const { rateFrom, rateTo } = this.getConversioRate();
  const ratio = rateFrom / rateTo;

  if (!isFinite(ratio) || ratio <= 0) {
    return { pointsNeeded: 0, accountBalance };
  }

  const pointsNeeded = dollarsValue * ratio;

  return {
    pointsNeeded: Math.ceil(pointsNeeded),
    accountBalance,
  };
}
}